/**
 * Types describing the Avatar Integration surface. This milestone ships only
 * the UI and state shape; the actual Unity renderer bridge is a future
 * integration (see AvatarRendererPort in features/avatar-integration).
 */

export type AvatarConnectionStatus = 'not-connected' | 'connecting' | 'connected' | 'error';

export type AvatarRendererEngine = 'unity';

export type AvatarOutputMode = 'local-preview' | 'stream-output' | 'both';

export type AvatarAutonomyMode = 'off' | 'assisted' | 'autonomous';

export type AvatarPlacementPreset = 'bottom-left' | 'bottom-right' | 'center' | 'custom';

export type AvatarVisibility = 'visible' | 'hidden';

export type OverlayLayerKind = 'camera' | 'avatar' | 'effects' | 'captions' | 'ui';

export interface OverlayLayer {
  id: OverlayLayerKind;
  label: string;
  order: number;
}

export interface AvatarIntegrationState {
  status: AvatarConnectionStatus;
  renderer: AvatarRendererEngine;
  overlayEnabled: boolean;
  outputMode: AvatarOutputMode;
  overlayOrder: OverlayLayer[];
}

export interface AvatarControlsState {
  status: AvatarConnectionStatus;
  renderer: AvatarRendererEngine;
  overlayEnabled: boolean;
  autonomy: AvatarAutonomyMode;
  placement: AvatarPlacementPreset;
  scalePercent: number;
  rotationDegrees: number;
  visibility: AvatarVisibility;
}

export const DEFAULT_OVERLAY_ORDER: OverlayLayer[] = [
  { id: 'camera', label: 'Camera', order: 1 },
  { id: 'avatar', label: 'Avatar', order: 2 },
  { id: 'effects', label: 'Effects', order: 3 },
  { id: 'captions', label: 'Captions', order: 4 },
  { id: 'ui', label: 'UI', order: 5 },
];

export const DEFAULT_AVATAR_INTEGRATION_STATE: AvatarIntegrationState = {
  status: 'not-connected',
  renderer: 'unity',
  overlayEnabled: true,
  outputMode: 'local-preview',
  overlayOrder: DEFAULT_OVERLAY_ORDER,
};

export const DEFAULT_AVATAR_CONTROLS_STATE: AvatarControlsState = {
  status: 'not-connected',
  renderer: 'unity',
  overlayEnabled: false,
  autonomy: 'off',
  placement: 'center',
  scalePercent: 100,
  rotationDegrees: 0,
  visibility: 'visible',
};
